import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createAuthenticatedApiClient } from '../../config/api';

// Define interfaces for moderation queue data
interface ModerationNote {
  _id: string;
  note: string;
  status: 'pending' | 'ai-approved' | 'moderator-approved' | 'rejected';
  isFlagged: boolean;
  flagCount: number;
  createdAt: string;
  updatedAt: string;
  contributorInfo: {
    userId: string;
    aliasName: string;
  };
  qualityScore?: number;
  moderation?: {
    aiDecision?: string;
    ai_review_notes?: string;
    moderatorComment?: string;
  };
}

interface QueueResponse {
  success: boolean;
  data: ModerationNote[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface BulkApproveResponse {
  success: boolean;
  approvedCount: number;
  excludedFlaggedCount: number;
  message: string;
}

interface ModerationQueueProps {
  onQuickAction?: (noteId: string, action: 'approve' | 'reject') => void;
}

const ModerationQueue: React.FC<ModerationQueueProps> = ({ onQuickAction }) => {
  // State management for queue data and filters
  const [notes, setNotes] = useState<ModerationNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [flaggedFilter, setFlaggedFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotes, setTotalNotes] = useState(0);
  const [pageSize] = useState(20);

  // Bulk approve states
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch moderation queue data
  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams({
        status: statusFilter,
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort: sortBy
      });
      
      if (flaggedFilter === 'true') {
        params.append('isFlagged', 'true');
      }
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Use the centralized API client
      const apiClient = createAuthenticatedApiClient();
      const response = await apiClient.get<QueueResponse>(`/moderator/notes/queue?${params}`);

      const data: QueueResponse = response.data;
      
      if (data.success) {
        setNotes(data.data);
        setTotalPages(data.pagination.pages);
        setTotalNotes(data.pagination.total);
      } else {
        throw new Error('Failed to load moderation queue');
      }
    } catch (err) {
      console.error('Error fetching moderation queue:', err);
      setError(err instanceof Error ? err.message : 'Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, flaggedFilter, searchTerm, sortBy, currentPage, pageSize]);

  // Effect to fetch data when filters or page change
  useEffect(() => {
    fetchQueue();
  }, [statusFilter, flaggedFilter, searchTerm, sortBy, currentPage, fetchQueue]);

  // Handle quick action buttons
  const handleQuickAction = async (noteId: string, action: 'approve' | 'reject') => {
    try {
      setSuccessMessage(null);
      const status = action === 'approve' ? 'moderator-approved' : 'rejected';

      // Use the centralized API client
      const apiClient = createAuthenticatedApiClient();
      await apiClient.put(`/moderator/notes/${noteId}/status`, {
        status,
        moderatorComment: `Quick ${action} from queue`
      });

      // Refresh the queue after action
      await fetchQueue();
      
      // Call optional callback
      if (onQuickAction) {
        onQuickAction(noteId, action);
      }
    } catch (err) {
      console.error(`Error ${action}ing note:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} note`);
    }
  };

  // Handle bulk approve action
  const handleBulkApprove = async () => {
    try {
      setBulkApproving(true);
      setError(null);
      setSuccessMessage(null);

      const apiClient = createAuthenticatedApiClient();
      const response = await apiClient.post<BulkApproveResponse>('/moderator/notes/bulk-approve');

      if (response.data.success) {
        const { approvedCount, excludedFlaggedCount } = response.data;
        let message = `Successfully approved ${approvedCount} note${approvedCount !== 1 ? 's' : ''}`;
        if (excludedFlaggedCount > 0) {
          message += ` (${excludedFlaggedCount} flagged note${excludedFlaggedCount !== 1 ? 's' : ''} excluded)`;
        }
        setSuccessMessage(message);
        setShowBulkApproveModal(false);
        await fetchQueue();
      }
    } catch (err) {
      console.error('Error bulk approving notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to bulk approve notes');
    } finally {
      setBulkApproving(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Truncate note content for display
  const truncateContent = (content: string, maxLength: number = 100): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Get status badge styling
  const getStatusBadge = (status: string, isFlagged: boolean) => {
    let badgeClass = 'badge ';
    
    switch (status) {
      case 'pending':
        badgeClass += 'bg-warning text-dark';
        break;
      case 'ai-approved':
        badgeClass += 'bg-info text-white';
        break;
      case 'moderator-approved':
        badgeClass += 'bg-success';
        break;
      case 'rejected':
        badgeClass += 'bg-danger';
        break;
      default:
        badgeClass += 'bg-secondary';
    }
    
    return (
      <div className="d-flex gap-1">
        <span className={badgeClass}>{status}</span>
        {isFlagged && <span className="badge bg-danger">Flagged</span>}
      </div>
    );
  };

  return (
    <div className="moderation-queue">
      {/* Header and Filters */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Moderation Queue</h2>
        <div className="d-flex align-items-center gap-3">
          {statusFilter === 'pending' && totalNotes > 0 && !loading && (
            <button
              className="btn btn-success btn-sm"
              onClick={() => setShowBulkApproveModal(true)}
              disabled={bulkApproving}
            >
              <i className="fas fa-check-double me-1"></i>
              Approve All Pending
            </button>
          )}
          <div className="text-muted">
            {totalNotes} total notes
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="row mb-4">
        <div className="col-md-3">
          <label htmlFor="statusFilter" className="form-label">Status</label>
          <select
            id="statusFilter"
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="ai-approved">AI Approved</option>
            <option value="moderator-approved">Moderator Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        <div className="col-md-2">
          <label htmlFor="flaggedFilter" className="form-label">Flagged</label>
          <select
            id="flaggedFilter"
            className="form-select"
            value={flaggedFilter}
            onChange={(e) => setFlaggedFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="true">Flagged Only</option>
          </select>
        </div>

        <div className="col-md-4">
          <label htmlFor="searchTerm" className="form-label">Search</label>
          <input
            type="text"
            id="searchTerm"
            className="form-control"
            placeholder="Search note content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label htmlFor="sortBy" className="form-label">Sort By</label>
          <select
            id="sortBy"
            className="form-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="createdAt">Creation Date</option>
            <option value="updatedAt">Last Updated</option>
            <option value="qualityScore">Quality Score</option>
            <option value="flagCount">Flag Count</option>
          </select>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
            aria-label="Close"
          />
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading moderation queue...</p>
        </div>
      ) : (
        <>
          {/* Notes Table */}
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Content</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Quality Score</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      No notes found matching current filters.
                    </td>
                  </tr>
                ) : (
                  notes.map((note) => (
                    <tr key={note._id}>
                      <td>
                        <div className="note-content">
                          <div className="fw-medium">
                            {truncateContent(note.note, 80)}
                          </div>
                          {note.moderation?.aiDecision && (
                            <small className="text-muted">
                              AI: {note.moderation.aiDecision}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="fw-medium">
                          {note.contributorInfo.aliasName || 'Anonymous'}
                        </span>
                      </td>
                      <td>
                        {getStatusBadge(note.status, note.isFlagged)}
                        {note.flagCount > 0 && (
                          <div className="small text-danger mt-1">
                            {note.flagCount} flag{note.flagCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </td>
                      <td>
                        {note.qualityScore !== undefined ? (
                          <span className={`badge ${note.qualityScore >= 70 ? 'bg-success' : note.qualityScore >= 40 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                            {note.qualityScore}
                          </span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        <small>{formatDate(note.createdAt)}</small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <Link
                            to={`/moderator/notes/${note._id}`}
                            className="btn btn-outline-primary"
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          {note.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-outline-success"
                                onClick={() => handleQuickAction(note._id, 'approve')}
                                title="Quick Approve"
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleQuickAction(note._id, 'reject')}
                                title="Quick Reject"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Moderation queue pagination">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Bulk Approve Confirmation Modal */}
      {showBulkApproveModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Bulk Approval</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBulkApproveModal(false)}
                  disabled={bulkApproving}
                />
              </div>
              <div className="modal-body">
                <p>
                  This will approve up to <strong>100</strong> pending notes.
                </p>
                <p className="text-muted small mb-0">
                  Flagged notes will be excluded and require individual review.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkApproveModal(false)}
                  disabled={bulkApproving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleBulkApprove}
                  disabled={bulkApproving}
                >
                  {bulkApproving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Approving...
                    </>
                  ) : (
                    'Approve All'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerationQueue; 